<template>
    <StandardLayout>
        <div class="admin-users">
            <DoubleHeader :title="$tc('pageTitles.adminUsers')" :backTitle="$t('admin.backBtn')" backRoute="adminMain" />

            <div class="search-bar">
                <div class="search-input-wrap">
                    <i class="icon icon-search search-icon" />
                    <input
                        type="text"
                        class="search-input"
                        v-model="query"
                        :placeholder="$tc('admin.users.searchPlaceholder')"
                        @input="onQueryInput"
                        autocomplete="off"
                    />
                    <button v-if="query" class="search-clear" @click="clearSearch">
                        <i class="icon-close" />
                    </button>
                </div>
                <button class="action-btn action-btn-solid" @click="openCreateModal">
                    {{ $tc("admin.users.createUser") }}
                </button>
            </div>

            <div v-if="busy" class="admin-loading-container">
                <Spinner />
                {{ $tc("admin.loadingData") }}
            </div>

            <template v-if="!busy">
                <div class="empty-state" v-if="users.length === 0">
                    <i class="icon-user" />
                    <p>{{ $tc("admin.users.noResults") }}</p>
                </div>

                <template v-if="users.length > 0">
                    <div class="card">
                        <div class="table-responsive">
                            <table class="users-table">
                                <thead>
                                    <tr>
                                        <th class="th-sortable" :class="sortClasses('id')" @click="sortColumn('id')">
                                            {{ $t("admin.users.id") }}
                                            <span class="sort-indicator" />
                                        </th>
                                        <th class="th-sortable" :class="sortClasses('name')" @click="sortColumn('name')">
                                            {{ $t("admin.users.name") }}
                                            <span class="sort-indicator" />
                                        </th>
                                        <th class="th-sortable" :class="sortClasses('email')" @click="sortColumn('email')">
                                            {{ $t("admin.users.email") }}
                                            <span class="sort-indicator" />
                                        </th>
                                        <th>{{ $t("admin.users.role") }}</th>
                                        <th class="cell-actions">{{ $t("admin.users.actions") }}</th>
                                    </tr>
                                </thead>
                                <tbody v-for="user in users" v-bind:key="user.id">
                                    <tr @click="selected(user)" :class="{ 'row-active': focused && focused.user.id === user.id }">
                                        <td class="cell-id">{{ user.id }}</td>
                                        <td class="cell-name">{{ user.name }}</td>
                                        <td class="cell-email">{{ user.email }}</td>
                                        <td class="cell-role">
                                            <span v-if="user.admin" class="role-badge role-admin">
                                                {{ $tc("admin.users.roleAdmin") }}
                                            </span>
                                            <span v-if="user.moderator" class="role-badge role-moderator">
                                                {{ $tc("admin.users.roleModerator") }}
                                            </span>
                                            <span v-if="!user.admin && !user.moderator" class="role-badge role-user">
                                                {{ $tc("admin.users.roleUser") }}
                                            </span>
                                        </td>
                                        <td class="cell-actions" @click.stop>
                                            <button class="action-btn action-btn-danger" @click="openDeleteModal(user)">
                                                {{ $t("admin.users.delete") }}
                                            </button>
                                            <button class="action-btn" @click="clearTnc(user)">
                                                {{ $t("admin.users.clearTnc") }}
                                            </button>
                                        </td>
                                    </tr>
                                    <tr v-if="focused && focused.user.id === user.id" class="expanded-row">
                                        <td colspan="6" class="expanded-cell">
                                            <div class="expanded-content">
                                                <div class="expanded-col-main">
                                                    <div class="expanded-section">
                                                        <h4 class="expanded-title">{{ $t("admin.users.stations") }}</h4>
                                                        <div v-if="focused.loadingDetails" class="section-loading">
                                                            <Spinner />
                                                        </div>
                                                        <template v-else>
                                                            <div
                                                                v-if="focused.stations && focused.stations.length === 0"
                                                                class="section-empty"
                                                            >
                                                                {{ $tc("admin.users.noStations") }}
                                                            </div>
                                                            <table
                                                                v-if="focused.stations && focused.stations.length > 0"
                                                                class="detail-table"
                                                            >
                                                                <thead>
                                                                    <tr>
                                                                        <th>{{ $t("admin.users.stationId") }}</th>
                                                                        <th>{{ $t("admin.users.stationName") }}</th>
                                                                        <th>{{ $t("admin.users.stationDeviceId") }}</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    <tr
                                                                        v-for="s in focused.stations"
                                                                        v-bind:key="s.id"
                                                                        class="detail-row-link"
                                                                        @click="openStation(s.id)"
                                                                    >
                                                                        <td>{{ s.id }}</td>
                                                                        <td class="cell-bold">{{ s.name }}</td>
                                                                        <td class="cell-device-id">
                                                                            <span class="device-id-text">{{ s.deviceId }}</span>
                                                                            <button
                                                                                class="copy-btn"
                                                                                @click.stop="copyStationDeviceId(s)"
                                                                                :title="$t('admin.stations.copyDeviceId')"
                                                                            >
                                                                                <i
                                                                                    :class="
                                                                                        copiedStationId === s.id
                                                                                            ? 'icon-icon-tick'
                                                                                            : 'icon-copy'
                                                                                    "
                                                                                ></i>
                                                                            </button>
                                                                        </td>
                                                                    </tr>
                                                                </tbody>
                                                            </table>
                                                        </template>
                                                    </div>
                                                </div>
                                                <div class="expanded-col-sidebar">
                                                    <div class="expanded-section">
                                                        <h4 class="expanded-title">{{ $t("admin.users.accountInfo") }}</h4>
                                                        <div class="info-grid">
                                                            <div class="info-row" v-if="user.tncDate">
                                                                <span class="info-label">{{ $t("admin.users.tncDate") }}</span>
                                                                <span class="info-value">{{ user.tncDate | prettyTime }}</span>
                                                            </div>
                                                            <div class="info-row" v-if="user.updatedAt">
                                                                <span class="info-label">{{ $t("admin.users.updatedAt") }}</span>
                                                                <span class="info-value">{{ user.updatedAt | prettyTime }}</span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div class="expanded-section" v-if="user.bio">
                                                        <h4 class="expanded-title">{{ $t("admin.users.bio") }}</h4>
                                                        <p class="bio-text">{{ user.bio }}</p>
                                                    </div>

                                                    <div class="expanded-section">
                                                        <div class="expanded-title-row">
                                                            <h4 class="expanded-title">{{ $t("admin.users.projects") }}</h4>
                                                            <button class="action-btn" @click="openAssignModal(user)">
                                                                {{ $tc("admin.users.assign") }}
                                                            </button>
                                                        </div>
                                                        <div v-if="focused.loadingDetails" class="section-loading">
                                                            <Spinner />
                                                        </div>
                                                        <template v-else>
                                                            <div
                                                                v-if="focused.projects && focused.projects.length === 0"
                                                                class="section-empty"
                                                            >
                                                                {{ $tc("admin.users.noProjects") }}
                                                            </div>
                                                            <div
                                                                v-if="focused.projects && focused.projects.length > 0"
                                                                class="info-grid info-grid-projects"
                                                            >
                                                                <router-link
                                                                    v-for="p in focused.projects"
                                                                    :key="p.id"
                                                                    :to="{ name: 'viewProject', params: { id: p.id } }"
                                                                    target="_blank"
                                                                    class="info-row info-row-link"
                                                                >
                                                                    <span class="info-label">{{ p.id }}</span>
                                                                    <span class="info-value">{{ p.name }}</span>
                                                                </router-link>
                                                            </div>
                                                        </template>
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div class="pagination-container" v-if="totalPages > 1">
                        <PaginationControls :page="page" :totalPages="totalPages" @new-page="onNewPage" />
                    </div>
                </template>
            </template>
        </div>

        <div v-if="createModal.show" class="modal-overlay" @click.self="closeCreateModal">
            <div class="create-modal">
                <div class="modal-header">
                    <h3>{{ $tc("admin.users.createTitle") }}</h3>
                    <button class="modal-close" @click="closeCreateModal"><i class="icon-close" /></button>
                </div>
                <form class="modal-body" autocomplete="off" @submit.prevent="confirmCreate">
                    <div class="info-notice">
                        <span class="info-notice-icon">?</span>
                        <span class="info-notice-text">{{ $tc("admin.users.createEmailNotice") }}</span>
                    </div>
                    <div class="form-field">
                        <label>{{ $tc("createAccount.form.name.label") }}</label>
                        <input
                            type="text"
                            v-model="createModal.name"
                            class="form-input"
                            :class="{ 'form-input-error': vc.name.$error }"
                            :name="fieldNames.name"
                            autocomplete="off"
                            data-lpignore="true"
                            data-1p-ignore
                            data-bwignore="true"
                            data-form-type="other"
                        />
                        <div v-if="vc.name.$error && !vc.name.required" class="field-error">
                            {{ $tc("createAccount.form.name.required") }}
                        </div>
                    </div>
                    <div class="form-field">
                        <label>{{ $tc("createAccount.form.email.label") }}</label>
                        <input
                            type="search"
                            v-model="createModal.email"
                            class="form-input"
                            :class="{ 'form-input-error': vc.email.$error }"
                            :name="fieldNames.email"
                            autocomplete="off"
                            data-lpignore="true"
                            data-1p-ignore
                            data-bwignore="true"
                            data-form-type="other"
                        />
                        <template v-if="vc.email.$error">
                            <div v-if="!vc.email.required" class="field-error">
                                {{ $tc("createAccount.form.email.required") }}
                            </div>
                            <div v-else-if="!vc.email.email" class="field-error">
                                {{ $tc("createAccount.form.email.valid") }}
                            </div>
                            <div v-else-if="!vc.email.taken" class="field-error">
                                {{ $tc("createAccount.form.email.taken") }}
                            </div>
                        </template>
                    </div>
                    <div class="form-field">
                        <label>{{ $tc("createAccount.form.password.label") }}</label>
                        <input
                            type="text"
                            v-model="createModal.password"
                            class="form-input form-input-masked"
                            :class="{ 'form-input-error': vc.password.$error }"
                            :name="fieldNames.password"
                            autocomplete="off"
                            data-lpignore="true"
                            data-1p-ignore
                            data-bwignore="true"
                            data-form-type="other"
                        />
                        <template v-if="vc.password.$error">
                            <div v-if="!vc.password.required" class="field-error">
                                {{ $tc("createAccount.form.password.required") }}
                            </div>
                            <div v-else-if="!vc.password.min" class="field-error">
                                {{ $tc("createAccount.form.password.valid") }}
                            </div>
                        </template>
                    </div>
                    <div class="form-field">
                        <label>{{ $tc("createAccount.form.passwordConfirmation.label") }}</label>
                        <input
                            type="text"
                            v-model="createModal.passwordConfirmation"
                            class="form-input form-input-masked"
                            :class="{ 'form-input-error': vc.passwordConfirmation.$error }"
                            :name="fieldNames.passwordConfirm"
                            autocomplete="off"
                            data-lpignore="true"
                            data-1p-ignore
                            data-bwignore="true"
                            data-form-type="other"
                        />
                        <template v-if="vc.passwordConfirmation.$error">
                            <div v-if="!vc.passwordConfirmation.required" class="field-error">
                                {{ $tc("createAccount.form.passwordConfirmation.required") }}
                            </div>
                            <div v-else-if="!vc.passwordConfirmation.sameAsPassword" class="field-error">
                                {{ $tc("createAccount.form.passwordConfirmation.valid") }}
                            </div>
                        </template>
                    </div>
                </form>
                <div class="modal-footer">
                    <button class="btn btn-secondary" @click="closeCreateModal">{{ $tc("admin.users.cancel") }}</button>
                    <button class="btn btn-primary" @click="confirmCreate" :disabled="createModal.busy">
                        {{ $tc("admin.users.createConfirm") }}
                    </button>
                </div>
            </div>
        </div>

        <div v-if="assignModal.show" class="modal-overlay" @click.self="closeAssignModal">
            <div class="assign-modal">
                <div class="modal-header">
                    <h3>{{ $tc("admin.users.assignTitle") }}</h3>
                    <button class="modal-close" @click="closeAssignModal"><i class="icon-close" /></button>
                </div>
                <div class="modal-body">
                    <div class="form-field">
                        <label>{{ $tc("admin.users.assignProjectLabel") }}</label>
                        <ProjectPicker @select="onProjectSelected" />
                    </div>
                    <div class="form-field">
                        <label>{{ $tc("admin.users.assignRoleLabel") }}</label>
                        <select v-model.number="assignModal.role" class="form-input">
                            <option :value="0">{{ $tc("admin.users.roleUser") }}</option>
                            <option :value="1">{{ $tc("admin.users.roleAdmin") }}</option>
                        </select>
                    </div>
                </div>
                <div class="modal-footer">
                    <button class="btn btn-secondary" @click="closeAssignModal">{{ $tc("admin.users.cancel") }}</button>
                    <button class="btn btn-primary" @click="confirmAssign" :disabled="!assignModal.project || assignModal.busy">
                        {{ $tc("admin.users.assignConfirm") }}
                    </button>
                </div>
            </div>
        </div>

        <div v-if="deleteModal.show" class="modal-overlay" @click.self="closeDeleteModal">
            <div class="delete-modal">
                <div class="modal-header">
                    <h3>{{ $tc("admin.users.deleteTitle") }}</h3>
                    <button class="modal-close" @click="closeDeleteModal"><i class="icon-close" /></button>
                </div>
                <form class="modal-body" autocomplete="off" @submit.prevent="confirmDelete">
                    <p class="delete-warning">
                        {{ $tc("admin.users.deleteWarning", 0, { name: deleteModal.user && deleteModal.user.name }) }}
                    </p>
                    <div class="form-field">
                        <label>{{ $tc("admin.users.adminPassword") }}</label>
                        <input
                            type="text"
                            v-model="deleteModal.password"
                            class="form-input form-input-masked"
                            :placeholder="$tc('admin.users.adminPasswordPlaceholder')"
                            autocomplete="off"
                            data-lpignore="true"
                            data-1p-ignore
                            data-bwignore="true"
                            data-form-type="other"
                        />
                    </div>
                    <div v-if="deleteModal.error" class="delete-error">{{ deleteModal.error }}</div>
                </form>
                <div class="modal-footer">
                    <button class="btn btn-secondary" @click="closeDeleteModal">{{ $tc("admin.users.cancel") }}</button>
                    <button class="btn btn-danger" @click="confirmDelete" :disabled="!deleteModal.password">
                        {{ $tc("admin.users.deleteConfirm") }}
                    </button>
                </div>
            </div>
        </div>
    </StandardLayout>
</template>

<script lang="ts">
import Vue from "vue";
import { required, email, minLength, sameAs } from "vuelidate/lib/validators";
import StandardLayout from "../StandardLayout.vue";
import CommonComponents from "@/views/shared";
import PaginationControls from "@/views/shared/PaginationControls.vue";
import ProjectPicker from "./ProjectPicker.vue";
import { SimpleUser, EssentialStation, AdminUserProject } from "@/api/api";
import { ActionTypes } from "@/store";
import { SnackbarStyle } from "@/store/modules/snackbar";
import { setBodyScrollLock } from "@/utilities";

interface FocusedUser {
    user: SimpleUser;
    stations: EssentialStation[] | null;
    projects: AdminUserProject[] | null;
    loadingDetails: boolean;
}

interface DeleteModal {
    show: boolean;
    user: SimpleUser | null;
    password: string;
    error: string;
}

interface CreateModal {
    show: boolean;
    name: string;
    email: string;
    password: string;
    passwordConfirmation: string;
    busy: boolean;
}

interface AssignModal {
    show: boolean;
    user: SimpleUser | null;
    project: AdminUserProject | null;
    role: number;
    busy: boolean;
}

export default Vue.extend({
    name: "AdminUsers",
    components: {
        ...CommonComponents,
        StandardLayout,
        PaginationControls,
        ProjectPicker,
    },
    props: {
        page: {
            type: Number,
            default: 0,
        },
        user: {
            type: Number,
            default: null,
        },
    },
    data(): {
        query: string;
        users: SimpleUser[];
        pageSize: number;
        totalPages: number;
        busy: boolean;
        focused: FocusedUser | null;
        deleteModal: DeleteModal;
        createModal: CreateModal;
        assignModal: AssignModal;
        available: boolean;
        searchTimer: number | null;
        copiedStationId: number | null;
        sortBy: string;
        sortDir: "asc" | "desc";
        fieldNames: { name: string; email: string; password: string; passwordConfirm: string };
    } {
        const r = () => `fk_${Math.random().toString(36).slice(2)}`;
        return {
            query: "",
            users: [],
            pageSize: 20,
            totalPages: 0,
            busy: false,
            focused: null,
            deleteModal: { show: false, user: null, password: "", error: "" },
            createModal: {
                show: false,
                name: "",
                email: "",
                password: "",
                passwordConfirmation: "",
                busy: false,
            },
            assignModal: { show: false, user: null, project: null, role: 0, busy: false },
            available: true,
            searchTimer: null,
            copiedStationId: null,
            sortBy: "id",
            sortDir: "desc",
            fieldNames: { name: r(), email: r(), password: r(), passwordConfirm: r() },
        };
    },
    validations: {
        createModal: {
            name: { required },
            email: {
                required,
                email,
                taken: function (this: any) {
                    return this.available;
                },
            },
            password: { required, min: minLength(10) },
            passwordConfirmation: { required, min: minLength(10), sameAsPassword: sameAs("password") },
        },
    },
    computed: {
        sortClasses(): (col: string) => Record<string, boolean> {
            return (col: string) => ({
                "th-sort-active": this.sortBy === col,
                "th-sort-asc": this.sortBy === col && this.sortDir === "asc",
                "th-sort-desc": this.sortBy === col && this.sortDir === "desc",
            });
        },
        vc(): any {
            return (this as any).$v.createModal;
        },
        isAnyModalOpen(): boolean {
            return this.createModal.show || this.deleteModal.show || this.assignModal.show;
        },
    },
    mounted(this: any) {
        this.refresh();
    },
    beforeDestroy(): void {
        setBodyScrollLock(false);
    },
    watch: {
        async page() {
            await this.refresh();
        },
        async user(val: number | null) {
            if (!val) {
                this.focused = null;
            } else {
                const found = this.users.find((u) => u.id === val);
                if (found && (!this.focused || this.focused.user.id !== val)) {
                    await this.expandUser(found);
                }
            }
        },
        isAnyModalOpen(val: boolean) {
            setBodyScrollLock(val);
        },
    },
    methods: {
        async copyStationDeviceId(station: { id: number; deviceId: string }): Promise<void> {
            await navigator.clipboard.writeText(station.deviceId);
            this.copiedStationId = station.id;
            setTimeout(() => {
                this.copiedStationId = null;
            }, 1500);
        },
        openStation(stationId: number): void {
            const route = this.$router.resolve({ name: "viewStationFromMap", params: { stationId: String(stationId) } });
            window.open(route.href, "_blank");
        },
        async refresh(): Promise<void> {
            this.busy = true;
            this.users = [];
            try {
                const result = await this.$services.api.adminGetUsers(
                    this.page,
                    this.pageSize,
                    this.query.trim() || undefined,
                    this.sortBy || undefined,
                    this.sortBy ? this.sortDir : undefined
                );
                this.totalPages = Math.ceil(result.total / this.pageSize);
                this.users = result.users;
            } finally {
                this.busy = false;
            }
        },
        sortColumn(col: string): void {
            if (this.sortBy === col) {
                this.sortDir = this.sortDir === "asc" ? "desc" : "asc";
            } else {
                this.sortBy = col;
                this.sortDir = "asc";
            }
            this.$router.replace({ name: "adminUsers", query: { page: "0" } }).catch(() => void 0);
            this.refresh();
        },
        onQueryInput() {
            if (this.searchTimer) clearTimeout(this.searchTimer);
            this.searchTimer = setTimeout(() => this.onSearch(), 300);
        },
        clearSearch() {
            this.query = "";
            this.focused = null;
            this.$router.replace({ name: "adminUsers", query: { page: "0" } }).catch(() => void 0);
            this.refresh();
        },
        onSearch(): void {
            this.focused = null;
            this.$router.replace({ name: "adminUsers", query: { page: "0" } }).catch(() => void 0);
            this.refresh();
        },
        selected(user: SimpleUser): void {
            if (this.focused && this.focused.user.id === user.id) {
                this.focused = null;
                this.$router.replace({ name: "adminUsers", query: { page: `${this.page}` } }).catch(() => void 0);
            } else {
                this.$router.replace({ name: "adminUsers", query: { page: `${this.page}`, user: `${user.id}` } }).catch(() => void 0);
                this.expandUser(user);
            }
        },
        async expandUser(user: SimpleUser): Promise<void> {
            this.focused = { user, stations: null, projects: null, loadingDetails: true };
            try {
                const [stationsResult, projectsResult] = await Promise.all([
                    this.$services.api.getAllStations(0, 1000, undefined, user.id),
                    this.$services.api.adminGetUserProjects(user.id),
                ]);
                if (this.focused && this.focused.user.id === user.id) {
                    this.focused.stations = stationsResult.stations;
                    this.focused.projects = projectsResult.projects;
                    this.focused.loadingDetails = false;
                }
            } catch {
                if (this.focused && this.focused.user.id === user.id) {
                    this.focused.stations = [];
                    this.focused.projects = [];
                    this.focused.loadingDetails = false;
                }
            }
        },
        onNewPage(newPage: number): void {
            window.scrollTo(0, 0);
            this.$router.push({ name: "adminUsers", query: { page: `${newPage}` } }).catch(() => void 0);
        },
        openDeleteModal(user: SimpleUser): void {
            this.deleteModal = { show: true, user, password: "", error: "" };
        },
        closeDeleteModal(): void {
            this.deleteModal = { show: false, user: null, password: "", error: "" };
        },
        async confirmDelete(): Promise<void> {
            const user = this.deleteModal.user;
            if (!user || !this.deleteModal.password) return;
            try {
                await this.$services.api.adminDeleteUser({
                    email: user.email,
                    password: this.deleteModal.password,
                });
                this.users = this.users.filter((u) => u.id !== user.id);
                if (this.focused && this.focused.user.id === user.id) {
                    this.focused = null;
                }
                this.closeDeleteModal();
            } catch {
                this.deleteModal.error = this.$tc("admin.users.deleteError");
            }
        },
        openCreateModal(): void {
            this.createModal = {
                show: true,
                name: "",
                email: "",
                password: "",
                passwordConfirmation: "",
                busy: false,
            };
            this.available = true;
            this.$nextTick(() => (this as any).$v.createModal.$reset());
        },
        closeCreateModal(): void {
            this.createModal = {
                show: false,
                name: "",
                email: "",
                password: "",
                passwordConfirmation: "",
                busy: false,
            };
            this.available = true;
            (this as any).$v.createModal.$reset();
        },
        async confirmCreate(): Promise<void> {
            this.available = true;
            const v = (this as any).$v.createModal;
            v.$touch();
            if (v.$pending || v.$error) return;
            this.createModal.busy = true;
            try {
                await this.$services.api.register({
                    name: this.createModal.name,
                    email: this.createModal.email,
                    password: this.createModal.password,
                    passwordConfirmation: this.createModal.passwordConfirmation,
                    tncAccept: true,
                });
                this.closeCreateModal();
                await this.refresh();
                await this.$store.dispatch(ActionTypes.SHOW_SNACKBAR, {
                    message: this.$tc("admin.users.createSuccess"),
                    type: SnackbarStyle.success,
                });
            } catch (error: any) {
                if (error && error.status === 400) {
                    this.available = false;
                } else {
                    await this.$store.dispatch(ActionTypes.SHOW_SNACKBAR, {
                        message: this.$tc("admin.users.createError"),
                        type: SnackbarStyle.fail,
                    });
                }
            } finally {
                this.createModal.busy = false;
            }
        },
        openAssignModal(user: SimpleUser): void {
            this.assignModal = { show: true, user, project: null, role: 0, busy: false };
        },
        closeAssignModal(): void {
            this.assignModal = { show: false, user: null, project: null, role: 0, busy: false };
        },
        onProjectSelected(project: AdminUserProject | null): void {
            this.assignModal.project = project;
        },
        async confirmAssign(): Promise<void> {
            if (!this.assignModal.user || !this.assignModal.project) return;
            this.assignModal.busy = true;
            try {
                await this.$services.api.adminAssignUserProject(
                    this.assignModal.user.id,
                    this.assignModal.project.id,
                    this.assignModal.role
                );
                const assignedUser = this.assignModal.user;
                this.closeAssignModal();
                if (this.focused && this.focused.user.id === assignedUser.id) {
                    const { projects } = await this.$services.api.adminGetUserProjects(assignedUser.id);
                    if (this.focused && this.focused.user.id === assignedUser.id) {
                        this.focused.projects = projects;
                    }
                }
                await this.$store.dispatch(ActionTypes.SHOW_SNACKBAR, {
                    message: this.$tc("admin.users.assignSuccess"),
                    type: SnackbarStyle.success,
                });
            } catch {
                this.assignModal.busy = false;
                await this.$store.dispatch(ActionTypes.SHOW_SNACKBAR, {
                    message: this.$tc("admin.users.assignError"),
                    type: SnackbarStyle.fail,
                });
            }
        },
        async clearTnc(user: SimpleUser): Promise<void> {
            await this.$confirm({
                message: this.$tc("admin.users.clearTncConfirm", 0, { name: user.name }),
                button: { no: this.$tc("admin.users.cancel"), yes: this.$tc("admin.users.clearTncConfirmBtn") },
                callback: async (confirm: boolean) => {
                    if (confirm) {
                        await this.$services.api.adminClearTermsAndConditions({ email: user.email });
                    }
                },
            });
        },
    },
});
</script>

<style scoped lang="scss">
@use "src/scss/variables";
@use "src/scss/mixins";

.admin-users {
    display: flex;
    flex-direction: column;
    padding: 40px 40px;
    max-width: 1080px;
    text-align: left;

    @include mixins.bp-down(variables.$lg) {
        padding: 40px 60px;
    }

    @include mixins.bp-down(variables.$md) {
        padding: 30px 30px;
    }
}

.search-bar {
    margin-top: 24px;
    margin-bottom: 4px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;

    .action-btn {
        padding: 7px 14px;
        font-size: 13px;
    }
}

.search-input-wrap {
    position: relative;
    display: flex;
    align-items: center;
    width: 320px;
    flex-shrink: 0;
}

.search-icon {
    position: absolute;
    left: 12px;
    pointer-events: none;

    &:before {
        font-size: 14px;
        color: var(--color-dark);
    }
}

.search-input {
    width: 100%;
    padding: 9px 36px 9px 36px;
    border: 1px solid var(--color-border);
    border-radius: 3px;
    font-size: 14px;
    font-family: var(--font-family);
    color: var(--color-dark);
    background: #fff;
    transition: border-color 0.15s ease;

    &:focus {
        outline: none;
        border-color: var(--color-primary);
    }

    &::placeholder {
        color: #9aa1ab;
    }
}

.search-clear {
    position: absolute;
    right: 10px;
    background: none;
    border: none;
    cursor: pointer;
    color: #9aa1ab;
    font-size: 12px;
    padding: 2px;
    display: flex;
    align-items: center;

    &:hover {
        color: var(--color-dark);
    }
}

.results-meta {
    font-size: 13px;
    color: #6a6d71;
    margin-top: 16px;
    margin-bottom: -8px;
}

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 200px;
    color: #9aa1ab;

    i {
        font-size: 32px;
        margin-bottom: 12px;
    }

    p {
        font-size: 14px;
        margin: 0;
    }
}

.card {
    background: #fff;
    border: 1px solid var(--color-border);
    border-radius: 3px;
    margin-top: 24px;
    overflow: hidden;
}

.table-responsive {
    overflow-x: auto;
}

.users-table {
    width: 100%;
    border-collapse: collapse;

    th,
    td {
        padding: 9px 12px;
        text-align: left;
        font-size: 13px;
    }

    th {
        font-family: var(--font-family-bold);
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        color: #6a6d71;
        border-bottom: 1px solid var(--color-border);
        white-space: nowrap;
    }

    th.th-sortable {
        cursor: pointer;
        user-select: none;

        &:hover {
            color: var(--color-dark);
        }

        &.th-sort-active {
            color: var(--color-primary);
        }

        .sort-indicator {
            display: inline-block;
            width: 0;
            height: 0;
            margin-left: 5px;
            vertical-align: middle;
            opacity: 0.25;
            border-left: 4px solid transparent;
            border-right: 4px solid transparent;
            border-top: 5px solid currentColor;
        }

        &:hover .sort-indicator {
            opacity: 0.5;
        }

        &.th-sort-asc .sort-indicator {
            opacity: 1;
            border-top: none;
            border-bottom: 5px solid currentColor;
        }

        &.th-sort-desc .sort-indicator {
            opacity: 1;
            border-top: 5px solid currentColor;
            border-bottom: none;
        }
    }

    td {
        border-bottom: 1px solid #f0f0f0;
        vertical-align: middle;
    }

    tbody:last-child tr:last-child td {
        border-bottom: none;
    }

    tbody tr {
        cursor: pointer;
        transition: background 0.12s ease;

        &:hover {
            background: #f9fafb;
        }

        &.row-active {
            background: #f4f5f7;
        }
    }
}

.cell-id {
    opacity: 0.5;
    width: 60px;
}

.cell-name,
.cell-email {
    white-space: nowrap;
}

.cell-bio {
    max-width: 240px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #6a6d71;
    font-size: 12px;
}

.cell-role {
    white-space: nowrap;
}

.cell-actions {
    width: 1%;
    white-space: nowrap;
    text-align: right;

    .action-btn + .action-btn {
        margin-left: 8px;
    }
}

.role-badge {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 3px;
    font-size: 11px;
    font-family: var(--font-family-medium);
    margin-right: 4px;

    &.role-admin {
        background: rgba(27, 128, 201, 0.1);
        color: var(--color-primary);
    }

    &.role-moderator {
        background: rgba(63, 133, 48, 0.1);
        color: #3f8530;
    }

    &.role-user {
        background: #f0f0f0;
        color: #6a6d71;
    }
}

.action-btn {
    padding: 5px 14px;
    font-size: 12px;
    font-family: var(--font-family-medium);
    color: var(--color-primary);
    background: transparent;
    border: 1px solid var(--color-primary);
    border-radius: 3px;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
    white-space: nowrap;

    &:hover {
        background: var(--color-primary);
        color: #fff;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
}

.action-btn-solid {
    background: var(--color-primary);
    color: #fff;
    border-color: var(--color-primary);

    &:hover {
        filter: brightness(0.9);
    }
}

.action-btn-danger {
    color: var(--color-danger);
    border-color: var(--color-danger);

    &:hover {
        background: var(--color-danger);
        color: #fff;
    }
}

.expanded-row {
    cursor: default !important;

    &:hover {
        background: transparent !important;
    }
}

.expanded-cell {
    padding: 0 !important;
    border-bottom: 2px solid var(--color-border) !important;
}

.expanded-content {
    display: flex;
    gap: 32px;
    padding: 24px;
    background: #f9fafb;

    @include mixins.bp-down(variables.$md) {
        flex-direction: column;
    }
}

.expanded-col-main {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.expanded-col-sidebar {
    flex: 0 0 auto;
    min-width: 320px;
    max-width: 480px;
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.expanded-section {
    min-width: 0;
}

.expanded-title {
    font-family: var(--font-family-bold);
    font-size: 14px;
    margin: 0 0 14px;
    padding-bottom: 8px;
    border-bottom: 1px solid var(--color-border);
}

.expanded-title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 14px;
    padding-bottom: 8px;
    border-bottom: 1px solid var(--color-border);

    .expanded-title {
        margin: 0;
        padding: 0;
        border: none;
    }

    .action-btn {
        padding: 3px 10px;
        font-size: 11px;
    }
}

.info-grid {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.info-grid-projects {
    gap: 0;
}

.info-row {
    display: flex;
    align-items: baseline;
    gap: 10px;
    font-size: 13px;
    margin-bottom: 4px;
}

.info-label {
    color: #6a6d71;
    white-space: nowrap;
    min-width: 96px;
}

.info-value {
    font-family: var(--font-family-medium);
}

.info-row-link {
    cursor: pointer;
    text-decoration: none;
    border-radius: 3px;
    transition: background 0.12s ease, color 0.12s ease;
    margin: 0;
    padding: 7px 5px;

    &:hover {
        background: rgba(27, 128, 201, 0.06);
        color: var(--color-primary);

        .info-label {
            color: var(--color-primary);
        }
    }

    .info-label {
        min-width: 22px;
    }
}

.bio-text {
    font-size: 13px;
    line-height: 1.6;
    color: #3e4044;
    margin: 0;
    white-space: pre-wrap;
}

.section-loading {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 16px 0;
    opacity: 0.5;

    ::v-deep .spinner {
        margin-right: 8px;

        div {
            border-color: var(--color-dark) transparent transparent transparent;
        }
    }
}

.section-empty {
    font-size: 13px;
    color: #9aa1ab;
    padding: 8px 0;
}

.detail-table {
    width: 100%;
    border-collapse: collapse;

    th,
    td {
        padding: 6px 10px;
        text-align: left;
        font-size: 12px;
    }

    th {
        font-family: var(--font-family-bold);
        font-size: 10px;
        text-transform: uppercase;
        letter-spacing: 0.4px;
        color: #6a6d71;
        border-bottom: 1px solid var(--color-border);
        white-space: nowrap;
    }

    td {
        border-bottom: 1px solid #f0f0f0;
    }

    tr:last-child td {
        border-bottom: none;
    }
}

.detail-row-link {
    cursor: pointer;
    transition: background 0.12s ease;

    &:hover td {
        background: rgba(27, 128, 201, 0.06);
        color: var(--color-primary);
    }
}

.cell-bold {
    font-family: var(--font-family-medium);
}

.cell-mono {
    font-family: monospace;
    font-size: 11px;
}

.cell-truncate {
    max-width: 160px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.cell-device-id {
    white-space: nowrap;

    &:hover .copy-btn {
        opacity: 1;
    }
}

.device-id-text {
    font-family: monospace;
    font-size: 11px;
    display: inline-block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: middle;
    cursor: default;
}

.copy-btn {
    opacity: 0;
    width: 22px;
    background: none;
    border: none;
    cursor: pointer;
    padding: 2px 4px;
    margin-left: 4px;
    color: #6a6d71;
    font-size: 12px;
    border-radius: 3px;
    vertical-align: middle;
    transition: opacity 0.15s ease, color 0.15s ease, background 0.15s ease;

    &:hover {
        color: var(--color-primary);
        background: rgba(27, 128, 201, 0.08);
    }

    .icon-icon-tick:before {
        color: #3f8530;
    }
}

.pagination-container {
    margin-top: 24px;
    display: flex;
    justify-content: center;
}

.modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

.delete-modal,
.create-modal,
.assign-modal {
    background: #fff;
    border-radius: 4px;
    max-width: 90vw;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.16);

    .modal-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 18px 24px;
        border-bottom: 1px solid var(--color-border);

        h3 {
            margin: 0;
            font-size: 15px;
            font-family: var(--font-family-bold);
        }
    }

    .modal-close {
        background: none;
        border: none;
        cursor: pointer;
        color: #9aa1ab;
        font-size: 14px;
        padding: 4px;
        line-height: 1;
        border-radius: 3px;
        transition: color 0.15s ease, background 0.15s ease;

        &:hover {
            color: var(--color-dark);
            background: #f0f0f0;
        }
    }

    .modal-body {
        padding: 24px;
    }

    .modal-footer {
        padding: 14px 24px;
        border-top: 1px solid var(--color-border);
        display: flex;
        gap: 10px;
        justify-content: flex-end;
    }
}

.delete-modal {
    width: 420px;
}

.create-modal {
    width: 400px;
    text-align: left;

    .modal-body {
        display: flex;
        flex-direction: column;
        gap: 16px;
    }
}

.assign-modal {
    width: 440px;
    text-align: left;

    .modal-body {
        display: flex;
        flex-direction: column;
        gap: 16px;
        overflow: visible;
    }
}

.delete-warning {
    font-size: 13px;
    margin: 0 0 16px;
    color: #3e4044;
}

.form-field {
    display: flex;
    flex-direction: column;
    gap: 6px;

    label {
        font-size: 13px;
        color: #6a6d71;
    }
}

.form-input {
    padding: 9px 12px;
    border: 1px solid var(--color-border);
    border-radius: 3px;
    font-size: 14px;
    font-family: var(--font-family);
    color: var(--color-dark);
    width: 100%;
    box-sizing: border-box;
    transition: border-color 0.15s ease;

    &:focus {
        outline: none;
        border-color: var(--color-primary);
    }

    &::placeholder {
        color: #9aa1ab;
    }
}

.delete-error {
    font-size: 13px;
    color: var(--color-danger);
}

.form-input-error {
    border-color: var(--color-danger) !important;
}

.form-input-masked {
    -webkit-text-security: disc;
    letter-spacing: 0.1em;
}

.info-notice {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    border-radius: 3px;
    border: solid 1px #cccdcf;
    background-color: #f8f9fa;
    font-size: 12px;
    color: #6a6d71;
}

.info-notice-icon {
    flex-shrink: 0;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background-color: #6a6d71;
    color: #fff;
    font-size: 10px;
    line-height: 14px;
    text-align: center;
    text-indent: -1px;
    opacity: 0.6;
    font-family: var(--font-family-medium);
}

.info-notice-text {
    line-height: 1.4;
}

.form-input[type="search"] {
    -webkit-appearance: none;
    appearance: none;

    &::-webkit-search-cancel-button,
    &::-webkit-search-decoration,
    &::-webkit-search-results-button,
    &::-webkit-search-results-decoration {
        display: none;
    }
}

.field-error {
    font-size: 12px;
    color: var(--color-danger);
}

.btn {
    padding: 8px 20px;
    font-size: 13px;
    font-family: var(--font-family-bold);
    border: none;
    border-radius: 3px;
    cursor: pointer;
    transition: opacity 0.15s ease;
    white-space: nowrap;

    &:hover {
        opacity: 0.88;
    }

    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }
}

.btn-primary {
    background: var(--color-primary);
    color: #fff;
}

.btn-secondary {
    background: #eaecef;
    color: #3e4044;
}

.btn-danger {
    background: var(--color-danger);
    color: #fff;
}
</style>
